import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
} from '@tanstack/react-router';
import { ClipboardCheck } from '@untitledui/icons/ClipboardCheck';
import { Flag05 } from '@untitledui/icons/Flag05';
import { Gift01 } from '@untitledui/icons/Gift01';
import { LogOut01 } from '@untitledui/icons/LogOut01';
import { Luggage02 } from '@untitledui/icons/Luggage02';
import { MessageCircle02 } from '@untitledui/icons/MessageCircle02';
import { Rows01 } from '@untitledui/icons/Rows01';
import { Settings02 } from '@untitledui/icons/Settings02';
import { Settings04 } from '@untitledui/icons/Settings04';
import { User01 } from '@untitledui/icons/User01';

import type { NavItem as NavItemType } from '@/components/navigation/header-navigation';

import { ImpersonationBanner } from '@/components/impersonation-banner/impersonation-banner';
import { HeaderNavigationBase } from '@/components/navigation/header-navigation';
import { BASE_PATH } from '@/constants/config';
import {
  participantInboxQueryOptions,
  useParticipantInbox,
} from '@/hooks/api/use-participant-inbox';
import {
  respondentMessagesQueryOptions,
  useRespondentMessages,
} from '@/hooks/api/use-respondent-messages';
import { useRespondentMessagesRealtime } from '@/hooks/api/use-respondent-messages-realtime';
import { dashboardExperimentFlagQueryOptions } from '@/lib/experiments/dashboard-matched-surfacing';
import { IntercomMessenger } from '@/lib/intercom/intercom-messenger';
import { getPublicEnv } from '@/utils/session/get-public-env';

import { ParticipantAgreementGate } from './-components/participant-agreement-gate';
import { useIsInterviewRoute } from './-hooks/use-is-interview-route';
import { useOnboardingExtrasRedirect } from './-hooks/use-onboarding-extras-redirect';
import {
  redirectResearchersToDashboard,
  redirectToParticipantLogin,
} from './-utils/participant-route-guards';

export const Route = createFileRoute('/participants/_authenticated')({
  beforeLoad: async ({ context, location, matches }) => {
    // Read from root context (already loaded in __root.tsx)
    const { user, profile, isAdminImpersonating, adminUser } = context;
    const publicEnv = await getPublicEnv();

    if (!user) {
      redirectToParticipantLogin(location);
    }

    redirectResearchersToDashboard(user, publicEnv);

    if (!user.isEmailVerified) {
      throw redirect({ to: '/participants/verify-email' });
    }

    if (!profile) {
      throw new Error('Failed to load profile');
    }

    if (!profile.isOnboardingComplete) {
      throw redirect({ to: '/participants/onboarding' });
    }

    if (profile.isPendingLead) {
      throw redirect({ to: '/participants/lead' });
    }

    // Started before the inbox await so the dashboard loader's flag lookup
    // overlaps it instead of adding a serial hop. Dashboard only.
    if (
      matches.some(
        (match) => match.routeId === '/participants/_authenticated/projects/',
      )
    ) {
      void context.queryClient.prefetchQuery(
        dashboardExperimentFlagQueryOptions(user.id),
      );
    }

    // Warm the unread badge before the layout paints — both the conversation
    // inbox and the pinned Respondent thread feed it. Failures are ignored:
    // the client hooks still fetch, and a missing badge is better than
    // blocking every participant page on messaging.
    await Promise.all([
      context.queryClient
        .prefetchQuery(participantInboxQueryOptions(user.id))
        .catch(() => null),
      context.queryClient
        .prefetchQuery(respondentMessagesQueryOptions(user.id))
        .catch(() => null),
    ]);

    // Return for child routes to use
    return {
      profileId: profile.id,
      user,
      isAdminImpersonating,
      adminUser,
    };
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { user, env, profile, isAdminImpersonating, adminUser } =
    Route.useRouteContext();
  const location = useLocation();
  const currentPath = location.pathname;

  // Trailing onboarding steps (photo, LinkedIn) are sticky: a returning
  // participant with steps still outstanding is routed back into onboarding,
  // at most once per tab session. The layout mounts once per full page load,
  // so this fires on "came back to the site", not on every navigation.
  useOnboardingExtrasRedirect(user, profile);

  // The nav badge covers everything unread in the inbox: regular
  // conversations (inbox summary) plus the pinned one-way Respondent thread,
  // which tracks its own unread count on a separate endpoint.
  const { data: inbox } = useParticipantInbox();
  const { unreadCount: respondentUnreadCount } = useRespondentMessages();
  const unreadCount = (inbox?.unreadCount ?? 0) + respondentUnreadCount;

  // Refetch the Respondent thread the moment core-api broadcasts a new
  // message for this user, so the badge doesn't wait for the next poll.
  useRespondentMessagesRealtime({
    appKey: env.PUSHER_APP_KEY,
    userId: user.id,
  });

  const navItems: NavItemType[] = [
    {
      label: 'Projects',
      href: `${BASE_PATH}/participants/projects`,
      current: currentPath.startsWith('/participants/projects'),
      icon: Rows01,
      testId: 'participant-nav-projects-link',
    },
    {
      label: 'My Projects',
      href: `${BASE_PATH}/participants/my-projects`,
      current: currentPath.startsWith('/participants/my-projects'),
      icon: ClipboardCheck,
      testId: 'participant-nav-my-projects-link',
    },
    {
      label: 'Messages',
      href: `${BASE_PATH}/participants/messages`,
      current: currentPath.startsWith('/participants/messages'),
      icon: MessageCircle02,
      badge:
        unreadCount > 0 ? (
          <span
            className="mt-1 size-2.5 shrink-0 rounded-full bg-brand-solid motion-safe:animate-pulse"
            aria-label="Unread messages"
          />
        ) : undefined,
      testId: 'participant-nav-messages-link',
    },
    {
      label: 'Settings',
      href: `${BASE_PATH}/participants/settings`,
      current: currentPath.startsWith('/participants/settings'),
      icon: Settings02,
      testId: 'participant-nav-profile-link',
      items: [
        {
          label: 'Profile info',
          href: `${BASE_PATH}/participants/settings?tab=profile-info`,
          icon: User01,
        },
        {
          label: 'Professional background',
          href: `${BASE_PATH}/participants/settings?tab=professional-background`,
          icon: Luggage02,
        },
        {
          label: 'Social accounts',
          href: `${BASE_PATH}/participants/settings?tab=social-accounts`,
          icon: Flag05,
        },
        {
          label: 'Account settings',
          href: `${BASE_PATH}/participants/settings?tab=account-settings`,
          icon: Settings04,
        },
        {
          label: 'Referrals',
          href: `${BASE_PATH}/participants/settings?tab=referrals`,
          icon: Gift01,
        },
        {
          divider: true,
        },
        {
          label: 'Sign out',
          href: `${BASE_PATH}/sign-out`,
          icon: LogOut01,
        },
      ],
    },
  ];

  // The interview route runs its own chrome (logo-only top bar, progress strip,
  // footer-pinned controls) and needs the full viewport, so we skip the global
  // header.
  const isInterview = useIsInterviewRoute();

  // Help chat for participants; skipped while impersonating so admins can't
  // read or send support messages as the participant.
  const intercom = (
    <IntercomMessenger
      appId={env.INTERCOM_APP_ID}
      user={user}
      enabled={!isAdminImpersonating}
    />
  );
  const agreementModal = (
    <ParticipantAgreementGate
      user={user}
      profile={profile}
      isAdminImpersonating={isAdminImpersonating}
    />
  );

  if (isInterview) {
    return (
      <div className="flex h-full flex-col bg-primary">
        {intercom}
        {agreementModal}
        <Outlet />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-primary">
      {intercom}
      {isAdminImpersonating && adminUser && (
        <ImpersonationBanner
          adminUser={adminUser}
          websiteUrl={env.WEBSITE_URL}
          impersonatedLabel={user.email}
        />
      )}
      <HeaderNavigationBase
        activeUrl={currentPath}
        items={navItems}
        showAvatarDropdown={true}
        user={user}
        logoHref={`${BASE_PATH}/participants/projects`}
      />
      <main className="flex-1 bg-secondary">
        {agreementModal}
        <Outlet />
      </main>
    </div>
  );
}
